import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Clock } from 'lucide-react';
import type { EventItem, Place } from '../../types/event';
import { ItineraryIcon, HotelItineraryIcon } from '../icons';
import { getTranslatedText, getTranslatedCategory } from '../../utils/translationHelpers';
import { getHotelItineraryUrl } from '../../utils/itineraryUtils';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useSection } from '../../hooks/useSection';

interface PlaceCardProps {
  place: Place;
  onClose: () => void;
  onEventClick: (event: EventItem, showIndex: number) => void;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  categoryColors: Map<string, string>;
}

const PlaceCard: React.FC<PlaceCardProps> = ({
  place,
  onClose,
  onEventClick,
  onCategorySelect,
  onTypeSelect,
  config,
  translations,
  categoryColors
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  const { trackEvent, trackItemAction, trackFilter } = useAnalytics({
    sectionId: section?.id,
    sectionName: section?.names?.fr,
    sectionType: 'event'
  });

  useEffect(() => {
    trackEvent('interaction', 'place_card_view', {
      place_name: place.name,
      place_address: place.fulladress,
      source: 'map'
    });
  }, [place.id]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Trier les spectacles par date
  const sortedShows = React.useMemo(() => {
    if (!place.shows) return [];
    
    return place.shows
      .map(show => ({
        ...show,
        events: show.events.sort((a, b) => {
          const dateCompare = a.date.localeCompare(b.date);
          return dateCompare !== 0 ? dateCompare : a.hour.localeCompare(b.hour);
        })
      }))
      .sort((a, b) => {
        const aEvent = a.events[0];
        const bEvent = b.events[0];
        const dateCompare = aEvent.date.localeCompare(bEvent.date);
        return dateCompare !== 0 ? dateCompare : aEvent.hour.localeCompare(bEvent.hour);
      });
  }, [place.shows]);

  return (
    <div 
      className="modal-container place-card-modal"
      onClick={handleOverlayClick}
    >
      <div 
        className="bg-white w-full max-w-2xl relative flex flex-col overflow-hidden"
        style={{ 
          borderRadius: config.border_radius,
          height: 'auto',
          maxHeight: '90vh'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header fixe */}
        <div className="sticky top-0 bg-white border-b z-10">
          <button
            onClick={onClose}
            className="absolute top-0 right-0 w-12 h-12 bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-opacity flex items-center justify-center"
            style={{ 
              borderTopRightRadius: config.border_radius,
              borderBottomLeftRadius: config.border_radius
            }}
          >
            <X size={20} />
          </button>

          <div className="p-6">
            <h2 className="text-2xl font-bold pr-8">{place.name}</h2>
            {place.fulladress && (
              <p className="text-gray-600 mt-1">{place.fulladress}</p>
            )}
          </div>
        </div>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto scrollbar-hide">
          <div className="p-6 space-y-8">
            {sortedShows.map((show, showIndex) => {
              const firstEvent = show.events[0];
              const categoryColor = categoryColors.get(firstEvent.category) || config['medium-color'];

              return (
                <div key={show.id} className="space-y-4">
                  {/* Show Info Block - Clickable */}
                  <div
                    className="flex gap-4 bg-gray-50 rounded-lg overflow-hidden hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => {
                      trackItemAction(firstEvent.id, 'item_click', 'event', {
                        item_name: show.name,
                        category: firstEvent.category,
                        type: firstEvent.type,
                        date: firstEvent.date,
                        time: firstEvent.hour,
                        show_id: show.id,
                        source: 'place_card'
                      });
                      onEventClick(firstEvent, showIndex);
                    }}
                  >
                    {/* Image */}
                    <div className="w-1/3 aspect-[3/2] relative">
                      <img
                        src={show.image}
                        alt={show.name}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Show Details */}
                    <div className="flex-1 p-4">
                      <h3 className="text-base md:text-xl font-semibold mb-2 line-clamp-2">{show.name}</h3>
                      
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            trackFilter('category_filter', firstEvent.category);
                            onCategorySelect(firstEvent.category);
                          }}
                          className="px-2 py-1 text-xs md:text-sm rounded text-white hover:opacity-80 transition-opacity"
                          style={{ backgroundColor: categoryColor }}
                        >
                          {getTranslatedCategory(translations, firstEvent.category, i18n.language)}
                        </button>
                        
                        {firstEvent.type && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              trackFilter('type_filter', firstEvent.type);
                              onTypeSelect(firstEvent.type);
                            }}
                            className="px-2 py-1 text-xs md:text-sm rounded text-white opacity-70 hover:opacity-80 transition-opacity"
                            style={{ backgroundColor: config['medium-color'] }}
                          >
                            {getTranslatedCategory(translations, firstEvent.type, i18n.language)}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Events Grid - Responsive */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {show.events
                      .sort((a, b) => {
                        const dateCompare = a.date.localeCompare(b.date);
                        return dateCompare !== 0 ? dateCompare : a.hour.localeCompare(b.hour);
                      })
                      .map(event => (
                        <div
                          key={event.id}
                          className="flex items-center justify-between px-3 py-2 bg-white rounded-lg hover:bg-gray-50 transition-colors text-left border border-gray-100 cursor-pointer"
                          onClick={() => {
                            trackItemAction(event.id, 'item_click', 'event', {
                              item_name: show.name,
                              category: event.category,
                              type: event.type,
                              date: event.date,
                              time: event.hour,
                              show_id: show.id,
                              source: 'place_card_date'
                            });
                            onEventClick(event, showIndex);
                          }}
                        >
                          <div className="flex items-center gap-2 truncate">
                            <Clock size={16} className="shrink-0 text-gray-500" />
                            <span className="truncate text-sm">
                              {new Intl.DateTimeFormat(i18n.language, {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                                hour: 'numeric',
                                minute: 'numeric'
                              }).format(new Date(`${event.date}T${event.hour}`))}
                            </span>
                          </div>
                          {event.price && (
                            <span 
                              className="text-xs md:text-sm font-medium px-2 py-0.5 rounded-full shrink-0 ml-2"
                              style={{ color: config['major-color'] }}
                            >
                              {event.price}
                            </span>
                          )}
                        </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer avec boutons d'action */}
        <div className="sticky bottom-0 bg-white border-t p-4">
          <div className="grid grid-cols-2 gap-3">
            {place.fulladress && (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(place.fulladress)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center px-4 py-2 rounded-lg text-white"
                style={{ backgroundColor: config['major-color'] }}
              >
                <ItineraryIcon size={18} className="mr-2" />
                {getTranslatedText(translations, 'btn_itinerary', i18n.language)}
              </a>
            )}

            {/* Ajouter le bouton d'itinéraire depuis l'hôtel */}
            {place.fulladress && config.fulladress && (
              <a
                href={getHotelItineraryUrl(place.fulladress, config.fulladress)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center px-4 py-2 rounded-lg text-white"
                style={{ backgroundColor: config['major-color'] }}
              >
                <HotelItineraryIcon size={18} className="mr-2" />
                {getTranslatedText(translations, 'btn_itinerary_hotel', i18n.language)}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaceCard;

export { PlaceCard }